import React from 'react';
import PropTypes from 'prop-types';

/**
 * Demo surfaces — the card / row / caption scaffolding every component page
 * builds its examples from.
 *
 * Replaces the "framed box" div and the demo+caption idiom that were
 * hand-repeated across the old single-file page.
 */

/** A titled card. Live components sit on white via `surface`. */
/** Stable anchor id from a card title, for the "on this page" jump row. */
export const cardSlug = (title) =>
  String(title)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const DemoCard = ({ title, hint, surface = true, flush = false, children }) => (
  // Only full-bleed cards clip: a padded card that clips cuts off the
  // dropdown of any Input, DatePicker or searchable demo inside it.
  <div className={`ds-page__card${flush ? ' ds-page__card--clip' : ''}`}>
    {(title || hint) && (
      <div className="ds-page__card-head" id={title ? cardSlug(title) : undefined}>
        {/* typo-h4, not typo-h5: h5 is uppercase, and nine shouting card
            titles on one page flattened the hierarchy against the h2 above. */}
        {title && <h3 className="typo-h4 ds-page__card-title">{title}</h3>}
        {hint && <span className="typo-body ds-page__card-hint">{hint}</span>}
      </div>
    )}
    <div
      className={[
        'ds-page__card-body',
        surface ? '' : 'ds-page__card-body--muted',
        flush ? 'ds-page__card-body--flush' : '',
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  </div>
);

DemoCard.propTypes = {
  /** Card heading */
  title: PropTypes.string,
  /** Right-aligned note in the card head */
  hint: PropTypes.string,
  /** White body so component surfaces read correctly (default true) */
  surface: PropTypes.bool,
  /** Remove body padding — for full-bleed demos like Header or Footer */
  flush: PropTypes.bool,
  children: PropTypes.node,
};

/** Wrapping row of demos. */
export const DemoRow = ({ align = 'start', children }) => (
  <div className={`ds-page__demo-row${align === 'center' ? ' ds-page__demo-row--center' : ''}`}>
    {children}
  </div>
);

DemoRow.propTypes = {
  /** Cross-axis alignment of the row items */
  align: PropTypes.oneOf(['start', 'center']),
  children: PropTypes.node,
};

/** A single demo with an optional caption underneath. */
export const Demo = ({ caption, center = false, children }) => (
  <div className={`ds-page__demo${center ? ' ds-page__demo--center' : ''}`}>
    {children}
    {caption && <span className="typo-body-sm ds-page__demo-caption">{caption}</span>}
  </div>
);

Demo.propTypes = {
  /** Label rendered under the example */
  caption: PropTypes.string,
  /** Centre the example above its caption */
  center: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * Fixed 393px frame that makes a component's own container queries fire on a
 * desktop viewport. The container-type lives in each component's CSS (e.g.
 * `.ds-header-mobile-preview`), so pass that class through `className`.
 *
 * Kept deliberately although no page uses it today: Header, TopBar and Footer
 * moved to `ViewportFrame`, which gives the preview a real viewport and is the
 * right tool when a component switches on `@media`. This one stays for
 * components that switch on `@container`, where a frame is all you need.
 */
export const MobilePreview = ({ className = '', children }) => (
  <div className={`ds-page__mobile-frame ${className}`.trim()}>{children}</div>
);

MobilePreview.propTypes = {
  /** The component's own `*-mobile-preview` class that sets container-type */
  className: PropTypes.string,
  children: PropTypes.node,
};
